import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SaveIcon from "@mui/icons-material/Save";
import Paper from "@mui/material/Paper";
import { Breakpoint, DialogProps as MUIDialogProps, PaperProps, Theme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled, SxProps, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Loader from "_shared/_views/loading/loader";
import React, { ReactNode, RefObject, useCallback } from "react";

const DIALOG_DRAG_HANDLE_SELECTOR = "#draggable-dialog-title";
const DIALOG_DRAG_EXCLUDE_SELECTOR = [
  "button",
  "a",
  "input",
  "textarea",
  "select",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="link"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="slider"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
  '[data-dialog-drag-exclude="true"]',
].join(",");

const Title = styled(DialogTitle)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 2),
  },
  alignItems: "center",
  cursor: "move",
  display: "flex",
  justifyContent: "space-between",
}));
const TitleNav = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
}));
const BackButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  zIndex: 999,
}));
const BackIcon = styled(ArrowBackIcon)(() => ({
  fontSize: "1.2em",
}));
const CloseButton = styled(IconButton)(() => ({
  zIndex: 999,
}));
const Content = styled(DialogContent)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

/** What MUI's dialog paper keeps clear of the window: 32px of margin on each side. */
export const DIALOG_PAPER_MARGIN = 64;

/**
 * What one column of a dialog's body takes.
 *
 * A number is that many pixels and no more. `flexible` is everything the other column leaves.
 * `hidden` is a column that is not drawn at all, which is how a side pane takes the whole of the
 * dialog without ever being drawn over the content it replaced.
 */
export type DialogPaneWidth = number | "flexible" | "hidden";

/**
 * Room a piece of content has asked the dialog to set aside beside itself.
 *
 * **Beside, never over.** The dialog answers this by laying its paper out as two columns under the
 * title bar, so a pane is a column of the dialog rather than a sheet floating on top of one. There
 * is no width at which the two overlap, which is the whole reason the request is shaped this way:
 * an overlay can be asked for by accident, and a column cannot.
 */
export type DialogSidePaneRequest = {
  /**
   * How wide the dialog should try to be, in pixels, or `null` to leave it as its caller opened it.
   *
   * A floor rather than an override: a dialog opened wider than this stays where it was, and the
   * window's own width caps both. A pane that is put away asks for `null` and the dialog goes back
   * to the width it was opened at.
   */
  dialogWidth: number | null;
  /** How wide the dialog's own column is. */
  content: DialogPaneWidth;
  /** How wide the pane's column is. */
  pane: DialogPaneWidth;
  /**
   * Whether the dialog draws its own content in the first column.
   *
   * A pane that has put something of its own in that column -- a rail standing in for the content
   * at a width that cannot hold the whole of it -- says false, and the dialog leaves its content
   * mounted and undrawn rather than unmounting it. So a reader who comes back to it comes back to
   * exactly what they left: not re-fetched, not re-laid out, not scrolled to the top.
   */
  drawContent: boolean;
};

/**
 * Whether two asks come to the same thing.
 *
 * Content that recomputes its ask on every render would otherwise hand the dialog a new object each
 * time, the dialog would re-render everything drawn on it to answer, and the answer would be a
 * fresh ask. This is what keeps that a request rather than a loop.
 */
export function sameSidePaneRequest(
  left: DialogSidePaneRequest | null,
  right: DialogSidePaneRequest | null,
): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  return (
    left.dialogWidth === right.dialogWidth
    && left.content === right.content
    && left.pane === right.pane
    && left.drawContent === right.drawContent
  );
}

/** One column of the dialog's body, as a grid track. */
function dialogPaneTrack(width: DialogPaneWidth): string {
  if (width === "flexible") return "minmax(0, 1fr)";
  if (width === "hidden") return "0px";
  return `${width}px`;
}

/**
 * The dialog body's two columns, as `grid-template-columns`.
 *
 * Always two tracks, whatever the pane is doing, so that a browser has a like-for-like pair to
 * interpolate between when the arrangement changes and the panes slide rather than jump.
 */
export function dialogPaneColumns(request: DialogSidePaneRequest): string {
  return `${dialogPaneTrack(request.content)} ${dialogPaneTrack(request.pane)}`;
}

/**
 * The width a dialog's paper takes while content has asked for room.
 *
 * Grow to fit, capped at the window. Never narrower than the caller opened it -- a request is a
 * floor, not an override, and a pane that needs less than the dialog already has should not shrink
 * the dialog around it. `null` for the opened width is MUI's "no maximum at all", which is already
 * the window and cannot be improved on.
 */
export function dialogPaperWidth(
  openedWidth: number | null,
  requestedWidth: number | null,
): string | undefined {
  const capped = `calc(100% - ${DIALOG_PAPER_MARGIN}px)`;
  if (requestedWidth === null) return undefined;
  if (openedWidth === null) return capped;
  return `min(${Math.max(openedWidth, requestedWidth)}px, ${capped})`;
}

/** The widths a dialog can be opened at, in pixels, from the breakpoint its caller named. */
export function openedDialogWidth(
  opened: Breakpoint | false | undefined,
  breakpoints: Theme["breakpoints"]["values"],
): number | null {
  if (opened === false) return null;
  // Undefined is MUI's own default, which is `sm`.
  return breakpoints[opened ?? "sm"];
}

/**
 * The dialog a piece of content is drawn on, offered to that content.
 *
 * Content inside a dialog is inside the dialog's scrolling box, which is the wrong place for a
 * surface that has to stay put while the reader scrolls — a side pane, for one. It is also inside
 * the dialog's focus trap, which is exactly the right place for it: a pane mounted anywhere else on
 * the page is unreachable by keyboard for as long as the dialog is open.
 *
 * So the dialog publishes the paper to mount into, which is its own positioning context and does
 * not scroll, and it takes a request for how to divide itself. The division is the dialog's own
 * work rather than the pane's: a pane that positioned itself would be free to position itself over
 * the content, and the whole point of the arrangement is that it cannot.
 */
export type DialogSurface = {
  /** The dialog's paper. `null` until it is mounted. */
  sidePanelContainer: HTMLElement | null;
  /**
   * Ask the dialog to set a column aside, and `null` to withdraw the ask.
   *
   * One request at a time: the last caller wins, and content that asks is expected to withdraw on
   * cleanup. Nothing in this application puts two panes on one dialog.
   */
  requestSidePane: (request: DialogSidePaneRequest | null) => void;
};

export const DialogSurfaceContext = React.createContext<DialogSurface | undefined>(undefined);

/** The dialog the calling content is drawn on, or `undefined` where it is not drawn on one. */
export function useDialogSurface(): DialogSurface | undefined {
  return React.useContext(DialogSurfaceContext);
}

/**
 * How wide the dialog has made the column its own content is drawn in.
 *
 * A number where a pane has taken the other column and named this one's width, `flexible` where the
 * content has whatever the pane left it, and `null` where no pane is out and the content has the
 * whole of the dialog.
 *
 * Published because content that lays itself out in columns has to answer to the room it was given
 * rather than to the window. The same profile is drawn at 516px inside the staffing picker and at
 * the width of the page on five other surfaces, and the window is no narrower in the first case
 * than in the second -- so a media query cannot tell the two apart, and content that read the
 * window would keep a four-track field grid in a pane with room for one.
 *
 * A stated number rather than a measured one, for the reason every width in this arrangement is a
 * stated number: jsdom measures nothing, and a number is a thing a test can set.
 */
export const DialogContentWidthContext = React.createContext<DialogPaneWidth | null>(null);

/** How much room the dialog gave the content this is drawn in, or `null` outside a dialog. */
export function useDialogContentWidth(): DialogPaneWidth | null {
  return React.useContext(DialogContentWidthContext);
}

export interface DialogProps {
  open?: boolean;
  /**
   * Identity of the thing this dialog is showing, for callers that can ask for it more than once.
   *
   * A dialog opened from a route rather than from a click has no single moment of asking: the
   * effect that opens it re-runs whenever the page re-renders past a changed dependency, and it
   * cannot see that its own dialog is already on the stack. Giving the dialog a key lets the
   * provider recognize the repeat and ignore it. Keys are only compared within the open stack, so
   * closing a dialog frees its key again.
   */
  dialogKey?: string;
  MuiProps?: Partial<MUIDialogProps>;
  component: React.ReactNode;
  title?: ReactNode | string;
  contentSxProps?: SxProps<Theme>;
  titleStyle?: React.CSSProperties;
  titleTypographyStyle?: React.CSSProperties;
  hideCloseButton?: boolean;
  hideBackButton?: boolean;
  hideFullScreenButton?: boolean;
  showSaveButton?: boolean;
  disableSave?: boolean;
  saveButtonTooltip?: string;
  onBack?: () => void;
  onClose?: () => void;
  isDirty?: boolean;
  isDirtyRef?: RefObject<boolean>;
  onSave?: (saveAbleItem?: unknown, preventNavigation?: boolean) => Promise<unknown>;
  saveAbleItem?: unknown;
  resetStack?: boolean;
}

export const DialogComponent = React.forwardRef(function DialogComponent(
  props: Readonly<DialogProps>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    open,
    MuiProps,
    component,
    title,
    contentSxProps,
    titleStyle,
    titleTypographyStyle,
    hideBackButton,
    hideFullScreenButton,
    hideCloseButton,
    showSaveButton,
    disableSave,
    saveButtonTooltip,
    saveAbleItem,
    isDirty,
    isDirtyRef,
    onBack,
    onClose,
    onSave,
  } = props;

  const confirm = useConfirmDialog();

  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  // A callback ref rather than a `useRef`, because the title bar is not in the document when this
  // component first commits: MUI's `Portal` renders nothing until a layout effect of its own has
  // found its container, so a ref read at commit time is null and nothing re-renders this component
  // afterwards to read it again. The callback fires when the node attaches, whenever that is.
  const [titleBar, setTitleBar] = React.useState<HTMLElement | null>(null);
  const [sidePane, setSidePane] = React.useState<DialogSidePaneRequest | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isSaveDisabled = disableSave || isSaving || !isDirty;

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const surface = React.useMemo<DialogSurface>(
    () => ({
      // The title bar is a direct child of the dialog's paper, which MUI positions `relative` -- so
      // the paper is both the element a pane mounts into and the grid it takes a column of.
      sidePanelContainer: titleBar?.parentElement ?? null,
      requestSidePane: (request: DialogSidePaneRequest | null) =>
        setSidePane((current) => (sameSidePaneRequest(current, request) ? current : request)),
    }),
    [titleBar],
  );

  const isFullScreen = MuiProps?.fullScreen ?? (isSmallScreen || isFullscreen);

  /**
   * The paper as two columns under its title bar, for as long as content has asked for one.
   *
   * **This is what makes a side pane a column rather than a sheet over the content.** The title bar
   * spans both columns and the pane starts in the second row, so the dialog's own Back, Fullscreen
   * and Close controls are never underneath anything; the content column is the first, so the pane
   * is beside it at every width, railed beside it, or -- when the content asks to be drawn not at
   * all -- pushed off by it. There is no arrangement here that puts one over the other.
   *
   * The width is the panes' own, capped at the window. Full screen is left alone: a dialog taking
   * the whole window has already answered the width question.
   */
  const paneLayoutSx = React.useMemo<SxProps<Theme> | undefined>(() => {
    if (!sidePane) return undefined;
    const paperWidth = isFullScreen
      ? undefined
      : dialogPaperWidth(
        openedDialogWidth(MuiProps?.maxWidth, theme.breakpoints.values),
        sidePane.dialogWidth,
      );
    const duration = theme.transitions.duration.enteringScreen;
    const easing = theme.transitions.easing.easeOut;

    return {
      "& .MuiDialog-paper": {
        display: "grid",
        gridTemplateColumns: dialogPaneColumns(sidePane),
        gridTemplateRows: "auto minmax(0, 1fr)",
        // The panes are sized to fit, so anything spilling sideways is a bug rather than something
        // to scroll to. Hiding it here is what keeps the promise that the dialog itself never gets
        // a horizontal scrollbar; the table inside the pane keeps its own.
        overflowX: "hidden",
        transition: `width ${duration}ms ${easing}, grid-template-columns ${duration}ms ${easing}`,
        ...(paperWidth ? { maxWidth: `calc(100% - ${DIALOG_PAPER_MARGIN}px)`, width: paperWidth } : {}),
        "& > .MuiDialogTitle-root": { gridColumn: "1 / -1", gridRow: 1 },
        "& > .MuiDialogContent-root": {
          gridColumn: 1,
          gridRow: 2,
          minWidth: 0,
          // Not drawn at all rather than merely narrow: `display: none` is what takes the content
          // out of the tab order and out of the accessibility tree along with the screen, so a
          // reader cannot tab into a profile that has been pushed off. React keeps it mounted, so
          // it comes back exactly as it was left.
          ...(sidePane.drawContent ? {} : { display: "none" }),
        },
      },
    };
  }, [MuiProps?.maxWidth, isFullScreen, sidePane, theme]);

  const muiSx = MuiProps?.sx;
  const dialogSx = React.useMemo<SxProps<Theme> | undefined>(() => {
    if (!paneLayoutSx) return muiSx;
    return [...(Array.isArray(muiSx) ? muiSx : [muiSx]), paneLayoutSx] as SxProps<Theme>;
  }, [muiSx, paneLayoutSx]);

  const handleCloseClicked = async () => {
    if (isDirty || isDirtyRef?.current) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) {
        return;
      }
      if (response === ConfirmResponse.AltOk) {
        await onSave?.(saveAbleItem, true);
        onClose?.();
        return;
      }
    }
    onClose?.();
  };

  const handleOnSave = useCallback(async () => {
    try {
      setIsSaving(true);
      await onSave?.(saveAbleItem);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, saveAbleItem]);

  return (
    <Dialog
      open={open ?? true}
      fullScreen={isSmallScreen || isFullscreen}
      ref={ref}
      disablePortal={false}
      closeAfterTransition={false}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      {...MuiProps}
      // After the spread: content drawn inside can ask for more room than the caller opened the
      // dialog with, and the ask has to survive the caller's own `maxWidth`. MUI's breakpoint
      // maximum is dropped outright while a pane is out, because the width the panes come to is a
      // number rather than one of five steps -- `dialogPaperWidth` keeps the caller's own width as
      // the floor, so nothing is given away by dropping it.
      maxWidth={sidePane?.dialogWidth == null ? MuiProps?.maxWidth : false}
      sx={dialogSx}
      keepMounted
    >
      <Title id="draggable-dialog-title" ref={setTitleBar} style={titleStyle}>
        <TitleNav>
          {!hideBackButton && (
            <Tooltip title="Back" placement="bottom">
              <span>
                <BackButton aria-label="back" onClick={onBack} size="medium">
                  <BackIcon />
                </BackButton>
              </span>
            </Tooltip>
          )}
          <Typography style={titleTypographyStyle} variant="h5">
            {title}
          </Typography>
        </TitleNav>
        <span>
          {!hideFullScreenButton && (
            <Tooltip title={isFullscreen ? "Restore" : "Fullscreen"} placement="bottom">
              <span>
                <IconButton aria-label="full screen" onClick={handleFullscreenToggle} size="medium">
                  {isFullscreen ? <FullscreenExitIcon fontSize="medium" /> : <FullscreenIcon fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showSaveButton && (
            <Tooltip title={saveButtonTooltip} placement="bottom">
              <span>
                <IconButton
                  aria-label="Save"
                  color="primary"
                  disabled={isSaveDisabled}
                  onClick={handleOnSave}
                  size="medium"
                >
                  {isSaving ? <Loader thickness={3} size={24} hideText /> : <SaveIcon fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!hideCloseButton && (
            <Tooltip title="Close" placement="bottom">
              <span>
                <CloseButton aria-label="close" onClick={handleCloseClicked} size="medium" edge="end">
                  <CloseIcon fontSize="large" />
                </CloseButton>
              </span>
            </Tooltip>
          )}
        </span>
      </Title>
      <Content sx={contentSxProps}>
        <DialogSurfaceContext.Provider value={surface}>
          <DialogContentWidthContext.Provider value={sidePane?.content ?? null}>
            {component}
          </DialogContentWidthContext.Provider>
        </DialogSurfaceContext.Provider>
      </Content>
    </Dialog>
  );
});

function PaperComponent(props: PaperProps) {
  const { className, style, onMouseDown, ...paperProps } = props;
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const boundsRef = React.useRef({ left: 0, top: 0, right: 0, bottom: 0 });
  const dragStartRef = React.useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null);
  const isFullscreen = Boolean(
    className?.includes("MuiDialog-paperFullScreen")
    || (props as PaperProps & { ownerState?: { fullScreen?: boolean } }).ownerState?.fullScreen,
  );
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  const updateBounds = useCallback((currentPosition: { x: number; y: number }) => {
    if (!nodeRef.current) {
      return boundsRef.current;
    }

    const rect = nodeRef.current.getBoundingClientRect();

    const nextBounds = {
      left: currentPosition.x - rect.left,
      top: currentPosition.y - rect.top,
      right: currentPosition.x + window.innerWidth - rect.right,
      bottom: currentPosition.y + window.innerHeight - rect.bottom,
    };

    boundsRef.current = nextBounds;
    return nextBounds;
  }, []);

  const clampPosition = useCallback((x: number, y: number, bounds = boundsRef.current) => ({
    x: Math.min(Math.max(x, bounds.left), bounds.right),
    y: Math.min(Math.max(y, bounds.top), bounds.bottom),
  }), []);

  const setClampedPosition = useCallback((x: number, y: number, bounds = boundsRef.current) => {
    setPosition((currentPosition) => {
      const nextPosition = clampPosition(x, y, bounds);
      if (currentPosition.x === nextPosition.x && currentPosition.y === nextPosition.y) {
        return currentPosition;
      }
      return nextPosition;
    });
  }, [clampPosition]);

  const shouldStartDrag = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (isFullscreen || event.button !== 0) return false;
    const target = event.target;
    if (!(target instanceof Element)) return false;
    if (!nodeRef.current?.contains(target)) return false;
    // Don't drag when clicking on interactive elements or elements marked with data-dialog-drag-exclude
    if (target.closest(DIALOG_DRAG_EXCLUDE_SELECTOR)) return false;
    return Boolean(target.closest(DIALOG_DRAG_HANDLE_SELECTOR));
  }, [isFullscreen]);

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    onMouseDown?.(event);
    if (!shouldStartDrag(event)) return;

    event.preventDefault();
    updateBounds(position);
    dragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: position.x,
      y: position.y,
    };
  }, [onMouseDown, position, shouldStartDrag, updateBounds]);

  React.useLayoutEffect(() => {
    const syncBounds = () => {
      setPosition((currentPosition) => {
        const nextBounds = updateBounds(currentPosition);
        const nextPosition = clampPosition(currentPosition.x, currentPosition.y, nextBounds);
        if (currentPosition.x === nextPosition.x && currentPosition.y === nextPosition.y) {
          return currentPosition;
        }
        return nextPosition;
      });
    };

    syncBounds();
    window.addEventListener("resize", syncBounds);
    return () => window.removeEventListener("resize", syncBounds);
  }, [clampPosition, updateBounds]);

  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const dragStart = dragStartRef.current;
      if (!dragStart) return;

      const nextX = dragStart.x + event.clientX - dragStart.pointerX;
      const nextY = dragStart.y + event.clientY - dragStart.pointerY;
      setClampedPosition(nextX, nextY);
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [setClampedPosition]);

  React.useLayoutEffect(() => {
    if (isFullscreen) {
      dragStartRef.current = null;
    }
  }, [isFullscreen]);

  const displayPosition = isFullscreen ? { x: 0, y: 0 } : position;

  return (
    <Paper
      {...paperProps}
      className={className}
      onMouseDown={handleMouseDown}
      ref={nodeRef}
      style={{
        ...style,
        transform: `translate3d(${displayPosition.x}px, ${displayPosition.y}px, 0)`,
      }}
    />
  );
}
